import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { newsletterSubscribers, type NewsletterSubscriber } from "@/mocks/admin";

function rowToSubscriber(row: Record<string, unknown>): NewsletterSubscriber {
  return {
    id: row.id as string,
    email: row.email as string,
    name: (row.name as string) || "",
    subscribedAt: (row.subscribed_at as string) || new Date().toISOString(),
    isActive: row.is_active as boolean,
    source: (row.source as string) || "website",
  };
}

export function useNewsletterDB() {
  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [usingMock, setUsingMock] = useState(false);

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("newsletter_subscribers")
        .select("*")
        .order("subscribed_at", { ascending: false });

      if (error || !data || data.length === 0) {
        setSubscribers(newsletterSubscribers);
        setUsingMock(true);
      } else {
        setSubscribers(data.map((r) => rowToSubscriber(r as Record<string, unknown>)));
        setUsingMock(false);
      }
    } catch {
      setSubscribers(newsletterSubscribers);
      setUsingMock(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const subscribe = useCallback(async (email: string, name?: string, source = "website"): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .insert({ email, name: name || "", source, is_active: true });
      if (error) return false;
      await fetchSubscribers();
      return true;
    } catch {
      return false;
    }
  }, [fetchSubscribers]);

  const unsubscribe = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .update({ is_active: false })
        .eq("id", id);
      if (error) return false;
      setSubscribers((prev) => prev.map((s) => (s.id === id ? { ...s, isActive: false } : s)));
      return true;
    } catch {
      return false;
    }
  }, []);

  const deleteSubscriber = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from("newsletter_subscribers").delete().eq("id", id);
      if (error) return false;
      setSubscribers((prev) => prev.filter((s) => s.id !== id));
      return true;
    } catch {
      return false;
    }
  }, []);

  return { subscribers, loading, usingMock, refetch: fetchSubscribers, subscribe, unsubscribe, deleteSubscriber };
}
